import type React from "react";
import {
  RefreshCw,
  Wifi,
  Radio,
  AlertCircle,
  Unplug,
  Clock,
  PauseCircle,
} from "lucide-react";
import { Button } from "../../client/components/ui/button";
import { Badge } from "../../client/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import useTranslation from "../../client/hooks/useTranslation";
import { cn } from "../../client/utils";
import { resolveSyncActions } from "./tradovateSyncActions";
import { resolveSyncPresentation } from "./tradovateSyncPresentation";
import type { SyncIcon, SyncTone } from "./tradovateSyncPresentation";

/**
 * Jumeau NON CONNECTE de `TradovateSyncStatus`, pour le mode demo.
 *
 * POURQUOI UN COMPOSANT SEPARE plutot qu'un `if` dans l'original :
 * `TradovateSyncStatus` resout sa connexion en interrogeant
 * `getTradovateConnections` par identifiant. Une connexion simulee n'y figure
 * pas, donc le composant rendrait `null`. Lui faire accepter une connexion
 * injectee obligerait a doubler tout son chemin d'etat pour un mode qui ne doit
 * pas exister en production.
 *
 * ⚠️ LA REGLE D'AFFICHAGE N'EST PAS DUPLIQUEE : badge comme actions viennent des
 * MEMES modules purs que le composant reel (`resolveSyncPresentation`,
 * `resolveSyncActions`). Seules changent la SOURCE des donnees (des props au
 * lieu d'une requete) et les handlers, qui sont des no-op.
 */

interface Props {
  displayStatus: string;
  realtimeStatus: string;
  lastSyncAt: Date | null;
  part: "badge" | "actions";
  /** Masque la ligne de fraîcheur, comme TradovateSyncStatus. */
  showFreshness?: boolean;
}

export default function TradovateDemoSyncStatus({
  displayStatus,
  realtimeStatus,
  lastSyncAt,
  part,
  showFreshness = true,
}: Props) {
  const t = useTranslation();

  const toneClass: Record<SyncTone, string> = {
    positive: "text-primary border-primary/30 bg-primary/10",
    warning:
      "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10",
    critical: "text-destructive border-destructive/30 bg-destructive/10",
    neutral: "text-muted-foreground border-border bg-muted/40",
  };

  const toneIcon: Record<SyncIcon, React.ReactNode> = {
    radio: <Radio className="h-3 w-3" />,
    wifi: <Wifi className="h-3 w-3" />,
    clock: <Clock className="h-3 w-3" />,
    pause: <PauseCircle className="h-3 w-3" />,
    alert: <AlertCircle className="h-3 w-3" />,
  };

  const presentation = resolveSyncPresentation({ displayStatus, realtimeStatus });
  const label = (t.tradovate as Record<string, string>)[presentation.labelKey] ?? displayStatus;
  const help = presentation.helpKey
    ? (t.tradovate as Record<string, string>)[presentation.helpKey]
    : undefined;
  const actions = resolveSyncActions({ displayStatus, realtimeStatus });

  if (part === "badge") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          title={help}
          className={cn(
            "flex w-fit shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium",
            toneClass[presentation.tone],
          )}
        >
          {toneIcon[presentation.icon]}
          {label}
        </Badge>

        {showFreshness && presentation.showFreshness && presentation.isLive && (
          <span className="text-xs text-muted-foreground">{t.tradovate.lastSyncLive}</span>
        )}

        {showFreshness && presentation.showFreshness && !presentation.isLive && lastSyncAt && (
          <span className="text-xs text-muted-foreground">
            {t.tradovate.lastSync}:{" "}
            {formatDistanceToNow(new Date(lastSyncAt), { addSuffix: true })}
          </span>
        )}
      </div>
    );
  }

  // ⚠️ BOUTONS INERTES. Ils sont rendus pour que la barre de pied ait sa vraie
  // densite visuelle, mais ne declenchent AUCUNE mutation : une connexion
  // simulee n'existe pas cote serveur.
  return (
    <div className="flex flex-wrap items-center gap-2">
      {actions.reconnect && (
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            "h-6 px-2 text-xs",
            displayStatus === "needs_reauth"
              ? "text-[hsl(var(--warning))]"
              : "text-muted-foreground",
          )}
          disabled
        >
          <RefreshCw className="mr-1 h-3 w-3" />
          {t.tradovate.reconnect}
        </Button>
      )}

      {actions.disconnect && (
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-muted-foreground" disabled>
          <Unplug className="mr-1 h-3 w-3" />
          {t.tradovate.disconnect}
        </Button>
      )}
    </div>
  );
}
